import type { ReactNodeViewProps } from '@tiptap/react';

import css from '@emotion/css';
import styled from '@emotion/styled';
import { useMemo } from 'react';
import { isMergeField } from 'utility/merge-fields';
import {
  isCategorizedMergeFieldList,
  useMergeFields,
} from '../use-merge-fields';
import { Pill, PillWrapper } from './PillNodeView';

const StyledPillWrapper = styled(PillWrapper)<{
  backgroundColor?: string | null;
  error?: boolean;
  selected?: boolean;
}>`
  height: unset;
  top: 1px;

  > span {
    ${({ selected, error }) => {
      if (selected && error) {
        return css`
          border-color: var(--color-viz-cat-7-primary);
        `;
      }
      if (selected) {
        return css`
          border-color: var(--color-viz-cat-2-primary);
        `;
      }
    }}
    ${({ backgroundColor }) =>
      backgroundColor &&
      css`
        background-color: ${backgroundColor};
      `}
  }
`;

const StyledPill = styled(Pill)<{ textDecoration?: string }>`
  span {
    color: unset;
    font-weight: unset;
  }

  ${({ textDecoration }) =>
    textDecoration?.length &&
    css`
      text-decoration: ${textDecoration};
    `}
`;

export const MergeFieldPill = (props: ReactNodeViewProps) => {
  const { deleteNode, editor, getPos, node, selected, extension } = props;
  const { editable } = editor.options;
  const { mergeFields, unavailableMergeFields = [] } = extension.options;
  const {
    attrs: { label, relationship, objectname },
  } = node;
  const { data, isLoading } = useMergeFields({ mergeFields });

  const error =
    !isMergeField.teamMember(relationship) &&
    !isMergeField.staticBusinessVariable(relationship) &&
    unavailableMergeFields.includes(relationship);

  const mergeField = useMemo(() => {
    if (isLoading) {
      return null;
    }

    if (isCategorizedMergeFieldList(data)) {
      return data
        .flatMap((x) => x.items)
        .find((field) => field.relationship === relationship);
    }

    return data.find((field) => {
      return field.relationship === relationship;
    });
  }, [data, relationship, isLoading]);

  const { textDecoration, backgroundColor } = useMemo(() => {
    const { decoration, highlight } = node.marks.reduce<{
      decoration: string[];
      highlight: string | null;
    }>(
      (acc, { type: { name }, attrs }) => {
        if (name === 'underline') acc.decoration.push('underline');
        if (name === 'strike') acc.decoration.push('line-through');
        if (name === 'textStyle') acc.highlight = attrs.highlight;
        return acc;
      },
      { decoration: [], highlight: null }
    );
    return { textDecoration: decoration.join(' '), backgroundColor: highlight };
  }, [node.marks]);

  const handleDelete = (_: string, ev: React.MouseEvent<HTMLElement>) => {
    ev.stopPropagation();
    deleteNode();
  };

  return (
    <StyledPillWrapper
      as="button"
      selected={selected}
      error={error}
      backgroundColor={backgroundColor}
      onClick={() => {
        const position = getPos();

        if (position !== undefined) {
          editor
            .chain()
            // Ensures a single position is selected (state.selection.empty = true)
            // Without this the entire editor content is selected, messing up the active marks
            .setTextSelection(position)
            .setNodeSelection(position) // this is what makes props.selected = true
            .run();
        }
      }}
    >
      <StyledPill
        hideRemoveIcon={!editable}
        maxWidth="250px"
        name={`{{ ${mergeField?.label ?? label} ${
          objectname ? `(${objectname}) ` : ''
        }}}`}
        onRemove={handleDelete}
        textDecoration={textDecoration}
        error={error}
      />
    </StyledPillWrapper>
  );
};
